'use client';
import React, { useEffect, useMemo, useState } from 'react';
import { useGed } from '@/lib/store';
import { fetchEcriture } from '@/lib/ecrire';
import { EEQ_RESULTS, vocabMeta, fmtDate, aujourdhuiIso } from '@/lib/meta';
import { card, inputStyle, RefISO, FilterBar, I, RequiredHint, enChargement, PAR_PAGE, ConfirmButton } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { Tableau } from '@/shared/Tableau';
import { pill, H1 } from './commun';
import { Norme } from '../../Norme';

const lblChamp: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
  letterSpacing: '.04em', color: 'var(--text-muted)', marginBottom: 4,
};

/**
 * Déclaration d'une évaluation externe reçue.
 *
 * Le registre n'avait aucune porte d'entrée : ses 88 lignes viennent toutes de
 * la reprise, et l'écran annonçait pourtant que « les campagnes se déclarent au
 * fil des rapports reçus ». Ce panneau est cette porte.
 *
 * Il est REPLIÉ par défaut et réservé au RQ : déclarer une EEQ engage
 * l'accréditation, ce n'est pas un champ de saisie qu'on laisse ouvert au
 * milieu d'un registre qu'on vient consulter.
 *
 * Le RÉSULTAT n'est pas saisi : |z| ≥ 3 vaut « non satisfaisant », ≥ 2
 * « à surveiller ». C'est le serveur qui tranche, et l'écran ne fait
 * qu'ANNONCER ce qu'il va trancher — un aperçu, jamais une seconde règle : deux
 * seuils écrits à deux endroits finissent par diverger.
 */
function DeclarerEeq({ onFerme }: { onFerme: () => void }) {
  const smqData = useGed((s) => s.smqData);
  const settings = useGed((s) => s.settings);
  const [programme, setProgramme] = useState('');
  const [organisme, setOrganisme] = useState('');
  const [discipline, setDiscipline] = useState('');
  const [analyte, setAnalyte] = useState('');
  const [dateEeq, setDateEeq] = useState(() => aujourdhuiIso());
  const [zscore, setZscore] = useState('');
  const [rapportRecu, setRapportRecu] = useState(true);
  const [envoi, setEnvoi] = useState(false);

  const z = Number.parseFloat(zscore.replace(',', '.'));
  const zValide = zscore.trim() !== '' && Number.isFinite(z);
  const valide = !!programme.trim() && !!analyte.trim() && zValide && /^\d{4}-\d{2}-\d{2}$/.test(dateEeq);
  const apercu = zValide
    ? (Math.abs(z) >= 3 ? 'non satisfaisant' : Math.abs(z) >= 2 ? 'à surveiller' : 'satisfaisant')
    : null;

  const enregistrer = async () => {
    setEnvoi(true);
    try {
      const ok = await smqData('ged/eeq', {
        programme: programme.trim(), organisme: organisme.trim(), discipline,
        analyte: analyte.trim(), dateEeq, zscore: z, rapportRecu,
      }, 'Évaluation externe enregistrée.');
      if (ok) onFerme();
    } finally { setEnvoi(false); }
  };

  const disciplines = (settings?.disciplines || []).filter((d) => d.active);

  return (
    <div style={{ ...card, padding: 20, marginTop: -6 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div>
          <label style={lblChamp} htmlFor="eeq-programme">Programme <RequiredHint /></label>
          <input id="eeq-programme" value={programme} onChange={(e) => setProgramme(e.target.value)} style={inputStyle} placeholder="ex. ProBioQual biochimie" />
        </div>
        <div>
          <label style={lblChamp} htmlFor="eeq-organisme">Organisme</label>
          <input id="eeq-organisme" value={organisme} onChange={(e) => setOrganisme(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={lblChamp} htmlFor="eeq-discipline">Discipline</label>
          <select id="eeq-discipline" value={discipline} onChange={(e) => setDiscipline(e.target.value)} style={inputStyle}>
            <option value="">—</option>
            {disciplines.map((d) => <option key={d.name} value={d.name}>{d.name}</option>)}
          </select>
        </div>
        <div>
          <label style={lblChamp} htmlFor="eeq-analyte">Analyte <RequiredHint /></label>
          <input id="eeq-analyte" value={analyte} onChange={(e) => setAnalyte(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={lblChamp} htmlFor="eeq-date">Date de la campagne <RequiredHint /></label>
          <input id="eeq-date" type="date" value={dateEeq} onChange={(e) => setDateEeq(e.target.value)} style={inputStyle} />
        </div>
        <div>
          <label style={lblChamp} htmlFor="eeq-z">z-score <RequiredHint /></label>
          <input id="eeq-z" value={zscore} onChange={(e) => setZscore(e.target.value)} style={inputStyle} inputMode="decimal" placeholder="ex. -1,8" />
        </div>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 12.5 }}>
        <input type="checkbox" checked={rapportRecu} onChange={(e) => setRapportRecu(e.target.checked)} style={{ accentColor: 'var(--color-primary)' }} />
        Le rapport de l’organisme est reçu
      </label>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '10px 0 0' }}>
        {apercu
          ? <>Résultat qui sera enregistré&nbsp;: <strong>{apercu}</strong> — calculé par le serveur à partir du z-score.</>
          : <>Le résultat (satisfaisant / à surveiller / non satisfaisant) est déduit du z-score par le serveur.</>}
      </p>
      <PiedActions
        style={{ marginTop: 14 }}
        annuler={{ onClick: onFerme, disabled: envoi }}
        principale={{ libelle: 'Créer', libelleChargement: 'Création…', chargement: envoi, disabled: !valide || envoi, onClick: () => void enregistrer() }}
      />
    </div>
  );
}

// EEQ — évaluation externe de la qualité (comparaisons inter-laboratoires).
/* ------------------------------ EEQ ------------------------------ */
export function Eeq() {
  const { eeqs, ncs, session, smqData, go, ouvrirPanneau, showToast, refresh, loading, booted } = useGed();
  const attend = enChargement(booted, loading, eeqs.length);
  const isRQ = session.roles.includes('rq');
  const [declarer, setDeclarer] = useState(false);

  // Le registre EEQ n'a pas de fiche de traitement à lui : le traitement se
  // fait dans la non-conformité. Cette NC est l'objet LIÉ au résultat qu'on
  // regarde : elle s'ouvre PAR-DESSUS le registre, qui garde ses filtres et sa
  // position. Sans NC retrouvée, il ne reste que le registre à proposer.
  const ouvrirNC = (ref: string, origine: string) => {
    const nc = ncs.find((n) => n.ref === ref);
    if (nc) ouvrirPanneau({ type: 'nc', valeur: nc.documentId, origine });
    else go('nc');
  };

  // Le registre s'allonge à chaque campagne du programme. Ce qu'on y cherche :
  // un analyte précis, et surtout les résultats qui posent problème — un
  // |z| ≥ 2 appelle une surveillance, ≥ 3 une action. D'où une pastille qui
  // regroupe ces deux cas plutôt que de laisser chercher à l'œil dans la colonne.
  const [q, setQ] = useState('');
  const [filtre, setFiltre] = useState('toutes');

  const aTraiter = eeqs.filter((x) => x.resultat !== 'satisfaisant');
  const sansRapport = eeqs.filter((x) => !x.rapportRecu);

  const liste = useMemo(() => {
    const base = filtre === 'atraiter' ? aTraiter : filtre === 'sansrapport' ? sansRapport : eeqs;
    const qq = q.trim().toLowerCase();
    return qq
      ? base.filter((x) => `${x.analyte} ${x.programme} ${x.organisme} ${x.discipline}`.toLowerCase().includes(qq))
      : base;
  }, [eeqs, q, filtre, aTraiter, sansRapport]);

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      {/* Le titre dit ce que l'écran PORTE — les résultats revenus — et non le
          domaine, que le chapeau annonce déjà. Son jumeau s'appelle
          « EEQ — Envois » : les deux faces se lisent ensemble. */}
      <H1 eyebrow={<>Comparaisons interlaboratoires · <RefISO code="7.3.7.3" prefixe="" /><Norme art="7.3.7.3" /></>} title="EEQ — Résultats reçus" />
      {isRQ && !declarer && (
        <div>
          <Bouton variante="primaire" onClick={() => setDeclarer(true)}>
            <I n="plus" /> Déclarer une évaluation reçue
          </Bouton>
        </div>
      )}
      {isRQ && declarer && <DeclarerEeq onFerme={() => setDeclarer(false)} />}
      {/* ⚠️ LE REPORT DES ÉCHANGES INTER-LABORATOIRES. La procédure l'exige :
          quand BIO24 participe à un EIL, le résultat entre dans ses EEQ EN
          MENTIONNANT que le programme est un « EIL local ». Sans cette mention,
          un consensus de six confrères se lirait, six mois plus tard, comme un
          rapport d'organisme accrédité. */}
      {isRQ && <ReprendreEil onRepris={() => void refresh()} />}
      <FilterBar
        q={q} onQ={setQ} placeholder="Rechercher un paramètre, un programme, un organisme…"
        chips={[
          { k: 'toutes', label: 'Toutes', n: eeqs.length },
          { k: 'atraiter', label: 'À traiter', n: aTraiter.length },
          { k: 'sansrapport', label: 'Rapport en attente', n: sansRapport.length },
        ]}
        actif={filtre} onChip={setFiltre}
        total={eeqs.length} affiches={liste.length}
        onReset={() => { setQ(''); setFiltre('toutes'); }} />
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        {/* Migré d'une grille de `<div>` vers le tableau du socle (28/08/2026).
            L'écran dessinait sept colonnes à la main, avec une fausse ligne
            d'en-tête : il n'avait donc ni tri, ni en-tête collant, ni
            déplacement des colonnes. Or trier par z-score est la PREMIÈRE chose
            qu'on fait sur une EEQ — c'est lui qui dit ce qui sort des limites. */}
        <Tableau
          id="eeq"
          ajusterLargeur
          /* L6 (10/10/2026) : niveau STANDARD — filtres (discipline, résultat en liste ;
             date du/au ; z-score min/max), masquage, états intégrés. */
          niveau="standard"
          chargement={attend}
          vide={{
            titre: eeqs.length === 0 ? 'Aucune EEQ enregistrée.' : 'Aucune EEQ ne correspond à ces critères.',
            indice: eeqs.length === 0
              ? 'Les campagnes d’évaluation externe se déclarent au fil des rapports reçus, avec le bouton « Déclarer une évaluation reçue » (réservé au responsable qualité).'
              : 'Élargissez la recherche ou revenez à « Toutes ».',
            action: eeqs.length > 0
              ? <Bouton variante="discret" taille="sm" onClick={() => { setQ(''); setFiltre('toutes'); }}>Réinitialiser les filtres</Bouton>
              : undefined,
          }}
          lignes={liste}
          cleLigne={(x) => x.documentId}
          parPage={PAR_PAGE}
          quoi="résultats"
          colonnes={[
            {
              cle: 'analyte', libelle: 'Programme · paramètre',
              valeur: (x) => x.analyte,
              style: { whiteSpace: 'normal', minWidth: 200 },
              rendu: (x) => (
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>{x.analyte}</span>
                  <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{x.programme}{x.rapportRecu ? '' : ' · rapport en attente'}</span>
                </span>
              ),
            },
            {
              cle: 'discipline', libelle: 'Discipline', type: 'liste',
              valeur: (x) => x.discipline || '',
              style: { fontSize: 12.5, width: 140 },
              rendu: (x) => x.discipline,
            },
            {
              cle: 'date', libelle: 'Date', type: 'date',
              // La date BRUTE : triée sur `fmtDate`, « 10/01 » passerait avant
              // « 09/12 ».
              valeur: (x) => x.dateEeq || '',
              style: { fontFamily: 'var(--font-mono)', fontSize: 12.5, whiteSpace: 'nowrap', width: 110 },
              rendu: (x) => fmtDate(x.dateEeq),
            },
            {
              cle: 'zscore', libelle: 'z-score', type: 'nombre',
              /* Trié sur la VALEUR ABSOLUE : ce qu'on cherche, c'est l'écart à
                 la cible, et −3,4 est aussi grave que +3,4. Un tri sur le signe
                 mettrait les pires résultats aux deux bouts de la liste. */
              valeur: (x) => Math.abs(x.zscore),
              style: { width: 90 },
              /* ⚠️ ARRONDI À DEUX DÉCIMALES, ET LA COLONNE S'ALIGNE (28/08/2026).
                 Le z-score partait BRUT à l'écran : saisi à la main depuis un
                 compte rendu, il faisait « -1,8 » et personne ne l'avait vu ;
                 calculé (report d'un EIL), il faisait
                 « 1.1208970766356112 » — dix-sept décimales dans une colonne de
                 90 px. Le défaut dormait parce que le registre était vide : il
                 est apparu au premier chiffre calculé qui y est entré.
                 ⚠️ Deux décimales, pas plus : un z-score se lit et se compare à
                 2 et à 3. Les décimales suivantes ne portent aucune information
                 et repoussent le SEUIL hors du champ visible.
                 `tabular-nums` : sans lui, « 1.12 » et « -0.07 » ne s'alignent
                 pas, et l'on compare des colonnes qui ne se superposent pas. */
              rendu: (x) => (
                <span style={{ fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', fontSize: 13, fontWeight: 700, color: Math.abs(x.zscore) >= 3 ? 'var(--red-600)' : Math.abs(x.zscore) >= 2 ? 'var(--amber-600)' : 'var(--green-600)' }}>
                  {Number.isFinite(x.zscore) ? x.zscore.toFixed(2) : '—'}
                </span>
              ),
            },
            {
              cle: 'resultat', libelle: 'Résultat', type: 'liste', libelleValeur: (v) => vocabMeta(EEQ_RESULTS, v).label,
              valeur: (x) => x.resultat || '',
              style: { width: 150 },
              rendu: (x) => pill(vocabMeta(EEQ_RESULTS, x.resultat)),
            },
            {
              /* Un résultat hors limites se traite comme une non-conformité —
                 même registre, même analyse de cause, même journal signé. La
                 colonne mène donc à SA NC, et le bouton la déclare. */
              cle: 'traitement', libelle: 'Traitement',
              valeur: (x) => x.ncRef || '',
              style: { width: 170 },
              rendu: (x) => (x.ncRef ? (
                <Bouton taille="sm" onClick={() => ouvrirNC(x.ncRef!, x.ref || `EEQ ${x.analyte} · ${x.programme}`)}
                  style={{ fontFamily: 'var(--font-mono)' }}>
                  {x.ncRef} →
                </Bouton>
              ) : x.resultat !== 'satisfaisant' && isRQ ? (
                /* Déclarer une NC CRÉE une fiche : ce n'est pas une suppression, donc pas de « danger »
                   (UI-CONVENTIONS §2). Le résultat hors limites se voit déjà dans sa colonne. */
                <Bouton taille="sm"
                  onClick={() => void (async () => {
                    const data = await smqData<{ ncRef?: string }>(
                      `ged/eeq/${x.documentId}/nc`, {}, 'Non-conformité déclarée.');
                    if (data?.ncRef) ouvrirNC(data.ncRef, x.ref || `EEQ ${x.analyte} · ${x.programme}`);
                  })()}>Déclarer une NC →</Bouton>
              ) : '—'),
            },
          ]}
          /* ── RETIRER UNE LIGNE (RQ, 26/08/2026) — en colonne d'ACTIONS (L6b, É-9) ──
             « Donner la main au RQ de supprimer des EEQ au besoin, comme il a la
             main pour en créer. » Suppression IRRÉVERSIBLE : variante danger +
             confirmation de l'application qui NOMME ce qui part ; le serveur
             écrit au journal ce que la ligne disait. */
          actions={isRQ ? (x) => (
                <ConfirmButton
                  label="" icon="trash" variant="danger"
                  titre={`Supprimer l’EEQ ${x.analyte} · ${x.programme}`}
                  question={`Supprimer définitivement cette EEQ (${x.analyte} · ${x.programme}${x.dateEeq ? ` · ${fmtDate(x.dateEeq)}` : ''}) ? Le retrait est écrit au journal ; la ligne, elle, ne se récupère pas.`}
                  confirmer="Supprimer"
                  onConfirm={async () => {
                    const r = await fetchEcriture(`/api/ged/ged/eeq/${x.documentId}`, { method: 'DELETE', credentials: 'include' });
                    if (!r.ok) {
                      showToast((await r.json().catch(() => null))?.error?.message || 'Suppression refusée.');
                      return;
                    }
                    showToast('EEQ supprimée — le retrait est au journal.');
                    void refresh();
                  }} />
          ) : undefined}

        />
      </div>
    </div>
  );
}

/**
 * REPRENDRE LES RÉSULTATS D'UN ÉCHANGE INTER-LABORATOIRES.
 *
 * ── Pourquoi ce panneau existe (30/08/2026) ────────────────────────────────
 *
 * L'EIL vit sur son propre domaine et sa propre base depuis le 29/08. Il
 * PRÉPARE les lignes d'EEQ ; il fallait venir les chercher. Sans ce panneau,
 * les lignes attendaient dans une file que personne ne regardait — et une file
 * qui ne se vide jamais est invisible.
 *
 * ⚠️ LE PANNEAU NE S'AFFICHE QUE S'IL Y A QUELQUE CHOSE À DIRE. Un bloc
 * permanent « 0 résultat à reprendre » ajouterait du bruit à un écran déjà
 * dense ; mais une PANNE de l'EIL, elle, se dit toujours — « rien à reprendre »
 * et « je n'ai pas pu regarder » ne sont pas la même phrase, et seule la
 * seconde demande une action.
 */
function ReprendreEil({ onRepris }: { onRepris: () => void }) {
  type Ligne = {
    id: number; campagne: string; ref: string; analyte: string;
    programme: string; resultat: string; zscore: number | null; sandbox: boolean;
  };
  const [lignes, setLignes] = useState<Ligne[] | null>(null);
  const [panne, setPanne] = useState('');
  const [message, setMessage] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const charger = React.useCallback(async () => {
    try {
      const r = await fetch('/api/eil-report', { cache: 'no-store' });
      const d = await r.json();
      /* Le pont non configuré n'est PAS une panne : c'est un état normal tant
         que l'EIL n'est pas relié. On se tait alors, plutôt que d'afficher une
         alerte pour quelque chose que personne n'a demandé. */
      if (d.configure === false) { setLignes([]); setPanne(''); return; }
      if (!r.ok) { setPanne(d.error || 'La file de l’EIL n’a pas pu être lue.'); setLignes(null); return; }
      setPanne(''); setLignes(d.lignes || []);
    } catch {
      setPanne('La file de l’EIL n’a pas pu être lue. Ceci n’est pas « rien à reprendre ».');
      setLignes(null);
    }
  }, []);

  /* ⚠️ FAUX POSITIF ASSUMÉ de `react-hooks/set-state-in-effect` (02/09/2026).
     `charger` est un `useCallback` ASYNC : son `setState` se produit APRÈS un
     `await`, donc dans une continuation, jamais dans le corps de l'effet. La
     règle ne franchit pas la frontière asynchrone et l'annonce quand même.
     C'est l'usage canonique d'un effet — « s'abonner à un système externe ».
     ⚠️ Le silence est CIBLÉ, ligne à ligne : désactiver la règle pour le
     fichier masquerait un vrai cas ajouté demain. */
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { void charger(); }, [charger]);

  if (panne) {
    return (
      <div style={{ ...card, padding: '11px 14px', fontSize: 12.5, lineHeight: 1.6, background: 'var(--amber-50)', borderColor: 'var(--amber-300)' }} role="alert">
        <strong>Résultats d’échanges inter-laboratoires : lecture impossible.</strong>
        <div style={{ marginTop: 4 }}>{panne}</div>
      </div>
    );
  }
  // Rien à reprendre, ou lecture en cours : on ne dit rien.
  if (!lignes || lignes.length === 0) return null;

  async function reprendre() {
    setEnvoi(true); setMessage('');
    try {
      /* ⚠️ `fetchEcriture` et non `fetch` : le témoin d'attente doit s'allumer
         comme partout ailleurs. La reprise interroge l'EIL PUIS écrit dans les
         EEQ — c'est le geste le plus long de cet écran, celui où l'absence de
         témoin fait recliquer, donc reprendre deux fois. */
      const r = await fetchEcriture('/api/eil-report', { method: 'POST' });
      const d = await r.json();
      if (!r.ok) { setMessage(d.error || 'La reprise a échoué.'); return; }
      /* On rapporte les TROIS comptes séparément : inscrites, accusées, et les
         échecs nommés. Un seul chiffre laisserait croire à un succès complet. */
      const bouts = [`${d.inscrites} ligne(s) inscrite(s) dans les EEQ`];
      /* ⚠️ Les doublons se DISENT. « 0 inscrite » sans explication se lirait
         comme un échec, alors que le résultat est bien là depuis un autre
         chemin — c'est le cas des deux lignes entrées à la main le 28/08. */
      if (d.doublons) bouts.push(`${d.doublons} déjà présente(s) dans le registre, non recréée(s)`);
      if (d.echecs?.length) {
        bouts.push(`${d.echecs.length} en échec : ${d.echecs.map((e: { ref: string; motif: string }) => `${e.ref} (${e.motif})`).join(', ')} — elles restent dans la file`);
      }
      if (d.avertissement) bouts.push(d.avertissement);
      setMessage(bouts.join(' · '));
      await charger();
      onRepris();
    } catch {
      setMessage('Le service est injoignable. Rien n’a été repris.');
    } finally { setEnvoi(false); }
  }

  const campagnes = [...new Set(lignes.map((l) => l.campagne))];

  return (
    <div style={{ ...card, padding: '12px 15px', display: 'grid', gap: 9 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>
        {lignes.length} résultat(s) d’échange inter-laboratoires à reprendre
      </div>
      <div style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-muted)' }}>
        Campagne(s) {campagnes.join(', ')}. Chaque ligne portera la mention{' '}
        <strong>« EIL local »</strong> et le nombre de participants — c’est ce qui empêchera de la
        lire comme un rapport d’organisme accrédité.
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--text-body)' }}>
        {lignes.map((l) => (
          <div key={l.id}>
            {l.ref} — {l.analyte}
            {l.zscore !== null ? ` · z = ${l.zscore.toLocaleString('fr-FR', { maximumFractionDigits: 2 })}` : ''}
            {' · '}{l.resultat}
            {/* ⚠️ La marque d'ESSAI vient de la CAMPAGNE, posée à la source. Une
                ligne d'essai inscrite sans le dire fausserait tous les comptages
                de la revue de direction. */}
            {l.sandbox && <strong style={{ color: 'var(--amber-700)' }}> · jeu d’essai</strong>}
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Bouton variante="primaire" taille="sm" chargement={envoi} libelleChargement="Reprise…" onClick={() => void reprendre()}>
          Reprendre dans les EEQ
        </Bouton>
        {message && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{message}</span>}
      </div>
    </div>
  );
}
